import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Trophy, ArrowRight, Check, Calendar, CheckCircle2, ClipboardCheck,
  Sparkles, Clock, MapPin, ChevronRight, AlertCircle,
} from 'lucide-react';
import sixLoveMark from '../../assets/6love-mark.svg';
import { LandingNav, Wordmark, LpButton, LOGIN_PATH } from './LandingNav';
import './landing.css';

/* ---------- Phone mockup (static dashboard preview) ---------- */
const PhoneMockup: React.FC = () => (
  <div className="lp-phone" aria-hidden="true">
    <div className="lp-phone-nav">
      <span className="lp-wordmark" style={{ gap: 7 }}>
        <img src={sixLoveMark} alt="" style={{ width: 26, height: 26, borderRadius: 8 }} />
        <span className="lp-wordmark-text" style={{ fontSize: 17 }}><span className="six">6</span>-love</span>
      </span>
      <span className="lp-avatar-sm">G</span>
    </div>
    <div className="lp-phone-scroll">
      <div style={{ marginBottom: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Good morning, Greg 👋</p>
        <h3 style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.02em' }}>Dashboard</h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
        <div className="lp-mini-stat">
          <span style={{ color: 'var(--accent)' }}><Calendar size={20} /></span>
          <div><div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>3</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Upcoming</div></div>
        </div>
        <div className="lp-mini-stat">
          <span style={{ color: 'var(--warn)' }}><AlertCircle size={20} /></span>
          <div><div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>2</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tasks</div></div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        <span style={{ color: 'var(--warn)', display: 'inline-flex' }}><AlertCircle size={16} /></span>
        <h4 style={{ fontSize: 15, fontWeight: 700 }}>Tasks</h4>
        <span className="lp-mini-pill" style={{ background: 'var(--warn-bg)', color: 'var(--warn-text)' }}>1</span>
      </div>
      <div className="lp-mini-card lp-mini-card--yellow" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Set availability</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Oakmont · Oct 12</div>
        </div>
        <span style={{ color: 'var(--text-faint)', display: 'inline-flex' }}><ChevronRight size={16} /></span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        <h4 style={{ fontSize: 15, fontWeight: 700 }}>Upcoming Matches</h4>
        <span className="lp-mini-pill" style={{ background: 'var(--green-100)', color: 'var(--green-700)' }}>3</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <MiniMatch opponent="Riverside Racquet Club" date="Sat, Oct 4" time="9:00 AM" place="Court 3 · Ridgeview" tag="vs" />
        <MiniMatch opponent="Oakmont Tennis Center" date="Sun, Oct 12" time="2:00 PM" place="Oakmont TC" tag="at" warn />
      </div>
    </div>
    <div className="lp-phone-fab" aria-hidden="true" />
  </div>
);

const MiniMatch: React.FC<{ opponent: string; date: string; time: string; place: string; tag: string; warn?: boolean }> =
  ({ opponent, date, time, place, tag, warn }) => (
    <div className={`lp-mini-match${warn ? ' lp-mini-match--warn' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          color: warn ? 'var(--warn-text)' : 'var(--green-700)' }}>{tag} {opponent.split(' ')[0]}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{opponent}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 7, fontSize: 11, color: 'var(--text-muted)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={12} /> {date}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> {time}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {place}</span>
      </div>
    </div>
  );

/* ---------- Hero ---------- */
const Hero: React.FC<{ goLogin: () => void }> = ({ goLogin }) => (
  <section className="lp-hero lp-hero--split" id="top">
    <div className="lp-ball lp-ball--a" />
    <div className="lp-ball lp-ball--b" />
    <div className="lp-hero-inner">
      <div className="lp-hero-copy">
        <span className="lp-eyebrow"><Trophy size={15} /> For tennis captains &amp; their teams</span>
        <h1 className="lp-h1">Run your tennis team<br /><span className="lp-accent-text">like a pro.</span></h1>
        <p className="lp-sub">
          6-love brings scheduling, availability, and lineups into one calm app —
          with an AI Co-Captain that drafts your strongest lineup in seconds.
        </p>
        <div className="lp-hero-cta">
          <LpButton variant="accent" size="lg" onClick={goLogin} iconRight={<ArrowRight size={18} />}>Get started free</LpButton>
          <LpButton variant="outline" size="lg" onClick={goLogin}>Log in</LpButton>
        </div>
        <p className="lp-trust"><Check size={15} /> Free for a limited time · No credit card needed</p>
      </div>
      <div className="lp-hero-art"><PhoneMockup /></div>
    </div>
  </section>
);

/* ---------- Features ---------- */
const FEATURES = [
  { Icon: Calendar, title: 'Schedule in one place', body: 'Matches, courts, and times live on one calendar your whole team can see — no more group-chat archaeology.' },
  { Icon: CheckCircle2, title: 'One-tap availability', body: 'Players tap Available, Maybe, or Can’t for each match. You see exactly who’s in at a glance.' },
  { Icon: ClipboardCheck, title: 'Lineups that publish', body: 'Build your doubles and singles lines, hit publish, and everyone is notified instantly.' },
];
const Features: React.FC = () => (
  <section className="lp-section" id="features">
    <div className="lp-container">
      <div className="lp-section-head">
        <span className="lp-kicker">Everything match day needs</span>
        <h2 className="lp-h2">Built for the work captains actually do</h2>
        <p className="lp-section-sub">From the first schedule import to the final lineup, 6-love keeps your season organized and your team in the loop.</p>
      </div>
      <div className="lp-features-grid">
        {FEATURES.map(({ Icon, title, body }) => (
          <div key={title} className="lp-feature-card">
            <span className="lp-feature-icon"><Icon size={24} /></span>
            <h3 className="lp-feature-title">{title}</h3>
            <p className="lp-feature-body">{body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- AI Co-Captain spotlight ---------- */
const CoCaptainSpotlight: React.FC = () => (
  <section className="lp-cc" id="cocaptain">
    <div className="lp-ball lp-ball--c" />
    <div className="lp-container lp-cc-inner">
      <div className="lp-cc-copy">
        <span className="lp-eyebrow lp-eyebrow--ondark"><Sparkles size={15} /> Your AI Co-Captain</span>
        <h2 className="lp-h2 lp-h2--ondark">Stuck on a lineup? Just ask.</h2>
        <p className="lp-cc-sub">
          Your Co-Captain weighs availability, ratings, and head-to-head matchups to suggest a
          winning lineup in seconds. Review it, tweak any line, and publish — you’re still the captain.
        </p>
        <ul className="lp-cc-list">
          <li><Check size={16} /> Lineup suggestions that respect who’s available</li>
          <li><Check size={16} /> Opponent intel pulled from past results</li>
          <li><Check size={16} /> Always a suggestion — never an auto-decision</li>
        </ul>
      </div>
      <div className="lp-cc-chat">
        <div className="lp-cc-chat-head">
          <span className="lp-cc-avatar" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>AI Co-Captain</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ridgeview 4.0 · vs Riverside</div>
          </div>
        </div>
        <div className="lp-bubble lp-bubble--me">Who should I play line 1 doubles Saturday?</div>
        <div className="lp-bubble lp-bubble--cc">
          Go with <b>Greg &amp; Marcus</b> at Line 1. They’re 4–1 together this season and Riverside’s
          top pair leans defensive — your net play should pressure them.
        </div>
        <div className="lp-bubble-actions">
          <LpButton variant="accent" size="sm" iconLeft={<Check size={14} />}>Use this line</LpButton>
          <LpButton variant="ghost" size="sm">See options</LpButton>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- How it works ---------- */
const STEPS = [
  { n: '01', title: 'Create your team', body: 'Add your roster and import your league schedule in a couple of minutes.' },
  { n: '02', title: 'Players set availability', body: 'Everyone taps their status for each match — captains see it update live.' },
  { n: '03', title: 'Build & publish lineups', body: 'Draft with your Co-Captain, publish, and the whole team gets notified.' },
];
const HowItWorks: React.FC = () => (
  <section className="lp-section lp-section--tint" id="how">
    <div className="lp-container">
      <div className="lp-section-head">
        <span className="lp-kicker">How it works</span>
        <h2 className="lp-h2">From roster to lineup in three steps</h2>
      </div>
      <div className="lp-steps">
        {STEPS.map((s, i) => (
          <div key={s.n} className="lp-step">
            <span className="lp-step-n">{s.n}</span>
            <h3 className="lp-step-title">{s.title}</h3>
            <p className="lp-step-body">{s.body}</p>
            {i < STEPS.length - 1 && <span className="lp-step-arrow"><ArrowRight size={20} /></span>}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Final CTA ---------- */
const FinalCTA: React.FC<{ goLogin: () => void }> = ({ goLogin }) => (
  <section className="lp-finalcta">
    <div className="lp-ball lp-ball--d" />
    <div className="lp-container lp-finalcta-inner">
      <h2 className="lp-finalcta-h">Ready to run your team like a pro?</h2>
      <p className="lp-finalcta-sub">Set up your team today — free for a limited time.</p>
      <div className="lp-hero-cta lp-hero-cta--center">
        <LpButton variant="accent" size="lg" onClick={goLogin} iconRight={<ArrowRight size={18} />}>Get started free</LpButton>
        <Link to={LOGIN_PATH} className="lp-finalcta-login">or log in →</Link>
      </div>
    </div>
  </section>
);

/* ---------- Footer ---------- */
export const Footer: React.FC = () => (
  <footer className="lp-footer">
    <div className="lp-container lp-footer-inner">
      <div className="lp-footer-brand">
        <Wordmark size={20} />
        <p className="lp-footer-tag">Run your tennis team like a pro.</p>
        <p className="lp-footer-note">Formerly Tennis Team Manager.</p>
      </div>
      <div className="lp-footer-cols">
        <div className="lp-footer-col">
          <span className="lp-footer-h">Product</span>
          <a href="#features">Features</a>
          <Link to="/pricing">Pricing</Link>
          <a href="#cocaptain">AI Co-Captain</a>
          <a href="#how">How it works</a>
        </div>
        <div className="lp-footer-col">
          <span className="lp-footer-h">Get started</span>
          <Link to={LOGIN_PATH}>Sign up</Link>
          <Link to={LOGIN_PATH}>Log in</Link>
        </div>
        <div className="lp-footer-col">
          <span className="lp-footer-h">Legal</span>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
      </div>
    </div>
    <div className="lp-footer-bottom">
      <div className="lp-container" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span>© 2026 6-love</span>
        <span>Made for captains and players who’d rather be on the court.</span>
      </div>
    </div>
  </footer>
);

/* ---------- Page ---------- */
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const goLogin = () => navigate(LOGIN_PATH);

  return (
    <div className="lp-page">
      <LandingNav atHome />
      <Hero goLogin={goLogin} />
      <Features />
      <CoCaptainSpotlight />
      <HowItWorks />
      <FinalCTA goLogin={goLogin} />
      <Footer />
    </div>
  );
};

export default LandingPage;
